import { useMemo } from 'react';
import {
  Body1Strong,
  Caption1,
  Dropdown,
  Field,
  Option,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';

import { MOCK_SOURCE_RECORDS } from '../../data';
import { SOURCE_TYPE_OPTIONS } from '../../constants';

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    maxHeight: '240px',
    overflowY: 'auto',
  },
  card: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingHorizontalM,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground1,
    cursor: 'pointer',
    textAlign: 'left',
    ':hover': { backgroundColor: tokens.colorNeutralBackground1Hover },
  },
  selected: { border: `2px solid ${tokens.colorBrandStroke1}`, backgroundColor: tokens.colorBrandBackground2 },
  muted: { color: tokens.colorNeutralForeground3 },
  preview: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    padding: tokens.spacingHorizontalM,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

type TProps = {
  sourceType: string;
  onTypeChange: (type: string) => void;
  selectedRecordId?: string;
  onRecordSelect: (id: string) => void;
};

export const SourceRecordPicker = ({ sourceType, onTypeChange, selectedRecordId, onRecordSelect }: TProps) => {
  const classes = useStyles();

  const records = useMemo(
    () => (sourceType ? MOCK_SOURCE_RECORDS.filter((record) => record.type === sourceType) : MOCK_SOURCE_RECORDS),
    [sourceType]
  );

  const selected = MOCK_SOURCE_RECORDS.find((record) => record.id === selectedRecordId);

  return (
    <div className={classes.root}>
      <Field label="Source type">
        <Dropdown
          placeholder="All source types"
          selectedOptions={[sourceType]}
          value={sourceType || 'All source types'}
          onOptionSelect={(_, data) => {
            onTypeChange((data.optionValue as string) ?? '');
          }}
        >
          <Option value="" text="All source types">
            All source types
          </Option>
          {SOURCE_TYPE_OPTIONS.map((type) => (
            <Option key={type} value={type} text={type}>
              {type}
            </Option>
          ))}
        </Dropdown>
      </Field>

      <div className={classes.list}>
        {records.map((record) => (
          <button
            key={record.id}
            type="button"
            className={mergeClasses(classes.card, selectedRecordId === record.id && classes.selected)}
            onClick={() => onRecordSelect(record.id)}
            aria-pressed={selectedRecordId === record.id}
          >
            <Caption1 className={classes.muted}>
              {record.reference} · {record.type}
            </Caption1>
            <Body1Strong>{record.title}</Body1Strong>
            <Caption1 className={classes.muted}>{record.summary}</Caption1>
          </button>
        ))}
        {records.length === 0 ? <Caption1 className={classes.muted}>No source records of this type.</Caption1> : null}
      </div>

      {selected ? (
        <div className={classes.preview}>
          <Caption1 className={classes.muted}>The draft will prefill these, and they stay editable:</Caption1>
          <Caption1>
            <span className={classes.muted}>Title: </span>
            {selected.title}
          </Caption1>
          <Caption1>
            <span className={classes.muted}>Reason or trigger: </span>
            {selected.summary}
          </Caption1>
          <Caption1>
            <span className={classes.muted}>Linked source: </span>
            {selected.reference}
          </Caption1>
        </div>
      ) : null}
    </div>
  );
};
